import { memo } from 'react';
import { Ticket, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useCredits } from '@/hooks/useCredits';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';

interface CreditBalanceDisplayProps {
  variant?: 'compact' | 'full';
  showBuyButton?: boolean;
  className?: string;
}

/**
 * Displays user's current credit balance
 * Used in navbar and other locations throughout the app
 */
const CreditBalanceDisplayComponent = ({
  variant = 'compact',
  showBuyButton = true,
  className,
}: CreditBalanceDisplayProps) => {
  const { balance, isLoading, isLow, isEmpty } = useCredits();
  const navigate = useNavigate();

  // Navigate to Account page, Credits section
  const handleBuyCredits = () => {
    navigate('/account');
  };

  if (isLoading) {
    return (
      <div className={cn("flex items-center gap-2", className)}>
        <div className="h-6 w-20 bg-muted animate-pulse rounded-full" />
      </div>
    );
  }

  // Determine badge styling based on balance state
  const badgeVariant = isEmpty 
    ? 'destructive' 
    : isLow 
      ? 'secondary' 
      : 'outline';

  const badgeClassName = cn(
    "flex items-center gap-1.5 px-3 py-1 font-medium transition-all cursor-pointer",
    isEmpty && "bg-destructive/10 text-destructive border-destructive/20",
    isLow && !isEmpty && "bg-secondary text-secondary-foreground border-secondary",
    !isEmpty && !isLow && "bg-background text-foreground border-border hover:bg-accent"
  );

  if (variant === 'full') {
    return (
      <div className={cn("flex flex-col items-center gap-2", className)}>
        <div className="flex items-center gap-2">
          <Ticket className="h-5 w-5 text-primary" />
          <span className="text-2xl font-bold">{balance}</span>
          <span className="text-muted-foreground">
            {balance === 1 ? 'credit' : 'credits'}
          </span>
        </div>
        {showBuyButton && (
          <Button onClick={handleBuyCredits} size="sm" variant="outline">
            <Plus className="h-4 w-4 mr-1" />
            Buy Credits
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <Badge 
        variant={badgeVariant}
        className={badgeClassName}
        onClick={handleBuyCredits}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && handleBuyCredits()}
      >
        <Ticket className="h-3.5 w-3.5" />
        <span>{balance} {balance === 1 ? 'credit' : 'credits'}</span>
      </Badge>
      
      {isEmpty && showBuyButton && (
        <Button 
          onClick={handleBuyCredits} 
          size="sm" 
          variant="default"
          className="h-7 px-2 text-xs"
        >
          <Plus className="h-3 w-3 mr-1" />
          Buy
        </Button>
      )}
    </div>
  );
};

CreditBalanceDisplayComponent.displayName = 'CreditBalanceDisplay';

export const CreditBalanceDisplay = memo(CreditBalanceDisplayComponent);
